import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { ApiErrorInfo, ApiResponse } from '../store/types';

const DEFAULT_REASON = 'Unknown error';

const pickMessageFromBody = (body: unknown): string | null => {
  if (!body || typeof body !== 'object') {
    return null;
  }

  const typed = body as Partial<ApiResponse> & {
    message?: unknown;
    error?: unknown;
  };

  if (Array.isArray(typed.errorsInfo) && typed.errorsInfo.length > 0) {
    const withMessage = (typed.errorsInfo as ApiErrorInfo[]).find(
      (item) => typeof item?.message === 'string' && item.message.length > 0
    );
    if (withMessage) {
      return withMessage.message;
    }
  }

  if (typed.errorCode && typeof typed.errorCode === 'object') {
    const message = Object.values(typed.errorCode).find(
      (value): value is string => typeof value === 'string' && value.length > 0
    );
    if (message) {
      return message;
    }
  }

  if (typeof typed.message === 'string' && typed.message.length > 0) {
    return typed.message;
  }

  if (typeof typed.error === 'string' && typed.error.length > 0) {
    return typed.error;
  }

  return null;
};

export const getApiErrorReason = (error: unknown): string => {
  if (error === null || error === undefined) {
    return DEFAULT_REASON;
  }

  if (typeof error === 'string' && error.length > 0) {
    return error;
  }

  if (typeof error !== 'object') {
    return DEFAULT_REASON;
  }

  const fromBody = pickMessageFromBody(error);
  if (fromBody) {
    return fromBody;
  }

  const fetchError = error as FetchBaseQueryError;
  if ('data' in fetchError) {
    const fromDataBody = pickMessageFromBody(fetchError.data);
    if (fromDataBody) {
      return fromDataBody;
    }
  }

  if ('status' in fetchError) {
    const { status } = fetchError;
    if (typeof status === 'string' && status.length > 0) {
      return status;
    }
    if (typeof status === 'number') {
      return `HTTP ${status}`;
    }
  }

  const serialized = error as SerializedError;
  if (typeof serialized.message === 'string' && serialized.message.length > 0) {
    return serialized.message;
  }

  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  return DEFAULT_REASON;
};
